import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  Mail,
  MousePointerClick,
  Users,
  Eye,
  Copy,
  Check,
  Link as LinkIcon,
  Plus,
  Trash2,
  ExternalLink,
  Loader2,
  Calendar,
  Globe,
  Smartphone,
  Monitor,
  Tablet,
  Info,
  Code,
  Download,
} from "lucide-react";
import { formatDate, formatRelativeTime, getCountryFlag, copyToClipboard } from "../lib/utils";

const CampaignDetailPage = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [newLinkOpen, setNewLinkOpen] = useState(false);
  const [newLink, setNewLink] = useState({ originalUrl: "", label: "" });
  const [recipientPage, setRecipientPage] = useState(1);
  const [eventPage, setEventPage] = useState(1);

  // Fetch campaign
  const { data: campaign, isLoading } = useQuery({
    queryKey: ["campaign", id],
    queryFn: () => api.get(`/campaigns/${id}`).then((r) => r.data),
  });

  // Fetch recipients (derived from events)
  const { data: recipientsData } = useQuery({
    queryKey: ["recipients", id, recipientPage],
    queryFn: () =>
      api.get(`/campaigns/${id}/recipients?page=${recipientPage}&limit=20`).then((r) => r.data),
    enabled: !!id,
  });

  // Fetch links
  const { data: links = [] } = useQuery({
    queryKey: ["links", id],
    queryFn: () => api.get(`/campaigns/${id}/links`).then((r) => r.data),
    enabled: !!id,
  });

  // Fetch events
  const { data: eventsData } = useQuery({
    queryKey: ["events", id, eventPage],
    queryFn: () =>
      api.get(`/campaigns/${id}/events?page=${eventPage}&limit=20`).then((r) => r.data),
    enabled: !!id,
  });

  // Update campaign status
  const updateStatusMutation = useMutation({
    mutationFn: (status) => api.put(`/campaigns/${id}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries(["campaign", id]);
      toast.success("Stato aggiornato");
    },
  });

  // Create link mutation
  const createLinkMutation = useMutation({
    mutationFn: (data) => api.post(`/campaigns/${id}/links`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["links", id]);
      setNewLinkOpen(false);
      setNewLink({ originalUrl: "", label: "" });
      toast.success("Link creato con successo!");
    },
    onError: () => {
      toast.error("Errore durante la creazione del link");
    },
  });

  // Delete link mutation
  const deleteLinkMutation = useMutation({
    mutationFn: (linkId) => api.delete(`/campaigns/${id}/links/${linkId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(["links", id]);
      toast.success("Link eliminato");
    },
  });

  const handleCopyPixel = async () => {
    if (campaign?.pixelTemplate) {
      await copyToClipboard(campaign.pixelTemplate);
      setCopied(true);
      toast.success("Snippet copiato negli appunti!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCreateLink = () => {
    if (!newLink.originalUrl || !newLink.label) {
      toast.error("URL e etichetta sono obbligatori");
      return;
    }
    createLinkMutation.mutate(newLink);
  };

  const handleExport = async () => {
    try {
      const response = await api.get(`/export/${id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${campaign?.name || "export"}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export completato!");
    } catch (error) {
      toast.error("Errore durante l'export");
    }
  };

  const getDeviceIcon = (device) => {
    switch (device?.toLowerCase()) {
      case "mobile":
        return <Smartphone className="h-4 w-4" />;
      case "tablet":
        return <Tablet className="h-4 w-4" />;
      default:
        return <Monitor className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      bozza: "bg-slate-100 text-slate-700",
      attiva: "bg-green-100 text-green-700",
      archiviata: "bg-orange-100 text-orange-700",
    };
    return (
      <Badge className={variants[status] || variants.bozza}>
        {status?.charAt(0).toUpperCase() + status?.slice(1) || "Bozza"}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Campagna non trovata</p>
        <Link to="/campaigns">
          <Button variant="link">Torna alle campagne</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaign-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Link to="/campaigns">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold font-heading">{campaign.name}</h1>
              {getStatusBadge(campaign.status)}
            </div>
            <p className="text-muted-foreground mt-1">{campaign.subject}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={campaign.status} onValueChange={(v) => updateStatusMutation.mutate(v)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bozza">Bozza</SelectItem>
              <SelectItem value="attiva">Attiva</SelectItem>
              <SelectItem value="archiviata">Archiviata</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Esporta CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{campaign.recipientCount || 0}</p>
                <p className="text-xs text-muted-foreground">Destinatari</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <Eye className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{campaign.uniqueOpens || 0}</p>
                <p className="text-xs text-muted-foreground">Aperture Uniche</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Mail className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{campaign.totalOpens || 0}</p>
                <p className="text-xs text-muted-foreground">Aperture Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-50 rounded-lg">
                <MousePointerClick className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{campaign.clickCount || 0}</p>
                <p className="text-xs text-muted-foreground">Click sui Link</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview">Panoramica</TabsTrigger>
          <TabsTrigger value="recipients">Destinatari</TabsTrigger>
          <TabsTrigger value="links">Link Tracker</TabsTrigger>
          <TabsTrigger value="events">Eventi</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Pixel Template */}
          <Card className="bg-white border border-slate-100">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Code className="h-5 w-5 text-primary" />
                Pixel di Tracking
              </CardTitle>
              <CardDescription>
                Copia questo snippet e incollalo nel template HTML della tua email
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Pixel Snippet Box */}
              <div className="relative">
                <pre className="bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                  {campaign.pixelTemplate}
                </pre>
                <Button
                  size="sm"
                  className="absolute top-2 right-2 bg-primary hover:bg-primary-hover"
                  onClick={handleCopyPixel}
                  data-testid="copy-pixel-btn"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Copiato!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1" />
                      Copia
                    </>
                  )}
                </Button>
              </div>

              {/* Platform Examples */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="space-y-3">
                    <p className="font-medium text-blue-900">
                      Come usare il pixel con diverse piattaforme
                    </p>
                    <div className="text-sm text-blue-800 space-y-2">
                      <p>
                        Sostituisci <code className="bg-blue-200 px-1 rounded">{"{{email}}"}</code> con
                        la variabile della tua piattaforma:
                      </p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>
                          <strong>Brevo:</strong>{" "}
                          <code className="bg-blue-200 px-1 rounded">{"{{contact.EMAIL}}"}</code>
                        </li>
                        <li>
                          <strong>Mailchimp:</strong>{" "}
                          <code className="bg-blue-200 px-1 rounded">{"*|EMAIL|*"}</code>
                        </li>
                        <li>
                          <strong>n8n:</strong>{" "}
                          <code className="bg-blue-200 px-1 rounded">{"{{$json.email}}"}</code>
                        </li>
                        <li>
                          <strong>App custom:</strong>{" "}
                          <code className="bg-blue-200 px-1 rounded">{"${email}"}</code> o la tua variabile
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Raw URL */}
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  URL Pixel (per uso avanzato)
                </Label>
                <div className="mt-1 bg-slate-100 p-3 rounded-lg font-mono text-xs break-all">
                  {campaign.pixelUrl}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Campaign Info */}
          <Card className="bg-white border border-slate-100">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Informazioni Campagna</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Creata il
                  </Label>
                  <p className="font-medium">{formatDate(campaign.createdAt)}</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Ultimo aggiornamento
                  </Label>
                  <p className="font-medium">{formatDate(campaign.updatedAt)}</p>
                </div>
              </div>
              {campaign.description && (
                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Descrizione
                  </Label>
                  <p className="mt-1">{campaign.description}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recipients Tab */}
        <TabsContent value="recipients">
          <Card className="bg-white border border-slate-100">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg">Destinatari</CardTitle>
                  <CardDescription>
                    Lista auto-popolata delle email che hanno aperto il messaggio
                  </CardDescription>
                </div>
                <Badge variant="outline" className="text-sm">
                  {recipientsData?.total || 0} destinatari
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {recipientsData?.recipients?.length > 0 ? (
                <>
                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Email</TableHead>
                          <TableHead>Prima Apertura</TableHead>
                          <TableHead>Ultima Apertura</TableHead>
                          <TableHead className="text-center">N° Aperture</TableHead>
                          <TableHead>Dispositivo</TableHead>
                          <TableHead>Paese</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {recipientsData.recipients.map((recipient, idx) => (
                          <TableRow key={recipient.email || idx}>
                            <TableCell className="font-medium">{recipient.email}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatRelativeTime(recipient.firstOpenedAt)}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatRelativeTime(recipient.lastOpenedAt)}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="secondary">{recipient.openCount}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getDeviceIcon(recipient.device)}
                                <span className="capitalize">{recipient.device || "N/D"}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1">
                                {getCountryFlag(recipient.country)} {recipient.country || "N/D"}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Pagination */}
                  {recipientsData.totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setRecipientPage((p) => Math.max(1, p - 1))}
                        disabled={recipientPage === 1}
                      >
                        Precedente
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Pagina {recipientPage} di {recipientsData.totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setRecipientPage((p) => Math.min(recipientsData.totalPages, p + 1))}
                        disabled={recipientPage === recipientsData.totalPages}
                      >
                        Successiva
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">Nessun destinatario ancora</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    I destinatari appariranno automaticamente quando apriranno l'email
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Links Tab */}
        <TabsContent value="links">
          <Card className="bg-white border border-slate-100">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg">Link Tracciati</CardTitle>
                  <CardDescription>
                    Crea link tracciabili per monitorare i click nelle tue email
                  </CardDescription>
                </div>
                <Dialog open={newLinkOpen} onOpenChange={setNewLinkOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-primary hover:bg-primary-hover">
                      <Plus className="h-4 w-4 mr-2" />
                      Nuovo Link
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Crea Link Tracciato</DialogTitle>
                      <DialogDescription>
                        Il link verrà abbreviato e ogni click sarà tracciato
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>URL Originale</Label>
                        <Input
                          placeholder="https://..."
                          value={newLink.originalUrl}
                          onChange={(e) =>
                            setNewLink({ ...newLink, originalUrl: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Etichetta</Label>
                        <Input
                          placeholder="Es: Bottone CTA, Link footer..."
                          value={newLink.label}
                          onChange={(e) => setNewLink({ ...newLink, label: e.target.value })}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setNewLinkOpen(false)}>
                        Annulla
                      </Button>
                      <Button
                        onClick={handleCreateLink}
                        disabled={createLinkMutation.isPending}
                        className="bg-primary hover:bg-primary-hover"
                      >
                        {createLinkMutation.isPending && (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        )}
                        Crea Link
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {links.length > 0 ? (
                <div className="space-y-3">
                  {links.map((link) => (
                    <div
                      key={link.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <LinkIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{link.label || "Link senza nome"}</span>
                          <Badge variant="secondary">{link.clickCount || 0} click</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 truncate">
                          {link.originalUrl}
                        </p>
                        <p className="text-xs text-primary font-mono mt-1">{link.trackingUrl}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            copyToClipboard(link.trackingUrl);
                            toast.success("Link copiato!");
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => window.open(link.originalUrl, "_blank")}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => deleteLinkMutation.mutate(link.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <LinkIcon className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">Nessun link tracciato</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Crea un link tracciato per monitorare i click
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events">
          <Card className="bg-white border border-slate-100">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Eventi di Apertura</CardTitle>
              <CardDescription>
                Storico completo di tutte le aperture per questa campagna
              </CardDescription>
            </CardHeader>
            <CardContent>
              {eventsData?.events?.length > 0 ? (
                <>
                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Email</TableHead>
                          <TableHead>Data</TableHead>
                          <TableHead>Dispositivo</TableHead>
                          <TableHead>Paese</TableHead>
                          <TableHead>Città</TableHead>
                          <TableHead>IP</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {eventsData.events.map((event) => (
                          <TableRow key={event.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{event.email || "N/D"}</span>
                                {event.isFirstOpen && (
                                  <Badge className="bg-green-100 text-green-700 text-xs">
                                    Prima
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatRelativeTime(event.openedAt)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getDeviceIcon(event.device)}
                                <span className="capitalize">{event.device || "N/D"}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {getCountryFlag(event.country)} {event.country || "N/D"}
                            </TableCell>
                            <TableCell>{event.city || "N/D"}</TableCell>
                            <TableCell className="font-mono text-xs">
                              {event.ipAddress || "N/D"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Pagination */}
                  {eventsData.totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEventPage((p) => Math.max(1, p - 1))}
                        disabled={eventPage === 1}
                      >
                        Precedente
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Pagina {eventPage} di {eventsData.totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEventPage((p) => Math.min(eventsData.totalPages, p + 1))}
                        disabled={eventPage === eventsData.totalPages}
                      >
                        Successiva
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <Eye className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">Nessun evento registrato</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Gli eventi appariranno quando i destinatari apriranno l'email
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CampaignDetailPage;
